const basePath = "security-appliances/";

export const securityAppliances = {
  id: 4,
  category: "Security Appliances",

  filters: {
    Brand: { type: "checkbox" },
    Model: { type: "checkbox" },
    Type: { type: "checkbox" },
    Throughput: { type: "checkbox" },
    Interfaces: { type: "checkbox" },
    "Form Factor": { type: "checkbox" },
    "Best For": { type: "checkbox" },
  },

  subcategories: [
    // ==========================
    // FORTINET
    // ==========================
    {
      name: "Fortinet FortiGate 60F Firewall",
      bestSeller: true,
      image: `${basePath}FortiGate_60F.png`,
      details: {
        Brand: "Fortinet",
        Model: "FortiGate 60F",
        Type: "Next-Generation Firewall",
        Throughput: "10 Gbps Firewall, 1 Gbps NGFW",
        Interfaces: "10 x GE RJ45",
        "Form Factor": "Desktop",
        "Best For": "Branch office / SMB",
      },
      about: `
        <ul>
          <li>Next-generation firewall with advanced threat protection.</li>
          <li>Ideal for SMB branch offices with SD-WAN support.</li>
        </ul>
      `,
    },
    {
      name: "Fortinet FortiGate 100F Firewall",
      bestSeller: true,
      image: `${basePath}FortiGate_100F.svg`,
      details: {
        Brand: "Fortinet",
        Model: "FortiGate 100F",
        Type: "Next-Generation Firewall",
        Throughput: "20 Gbps Firewall, 1.6 Gbps NGFW",
        Interfaces: "22 x GE RJ45, 4 x GE SFP",
        "Form Factor": "1U Rack",
        "Best For": "Mid-size campus",
      },
      about: `
        <ul>
          <li>Rack-mount NGFW sized for a mid-market head office or campus edge.</li>
          <li>Hardware-accelerated inspection keeps throughput high with IPS and application control enabled.</li>
          <li>Built-in SD-WAN with per-application steering across multiple WAN links.</li>
          <li>Integrates with FortiAnalyzer and FortiManager for central logging and policy.</li>
        </ul>
      `,
    },
    {
      name: "Fortinet FortiGate 200F Firewall",
      bestSeller: false,
      image: `${basePath}FortiGate_200F.svg`,
      details: {
        Brand: "Fortinet",
        Model: "FortiGate 200F",
        Type: "Next-Generation Firewall",
        Throughput: "27 Gbps Firewall, 3 Gbps NGFW",
        Interfaces: "18 x GE RJ45, 8 x GE SFP, 4 x 10GE SFP+",
        "Form Factor": "1U Rack",
        "Best For": "Datacenter edge",
      },
      about: `
        <ul>
          <li>High-throughput NGFW for datacenter and large-campus perimeters.</li>
          <li>10GE SFP+ uplinks for aggregated internet and inter-site links.</li>
          <li>Redundant power supply option for always-on environments.</li>
          <li>Supports zero-trust network access policies alongside traditional firewalling.</li>
        </ul>
      `,
    },

    // ==========================
    // SOPHOS
    // ==========================
    {
      name: "Sophos XGS 2100 Firewall",
      bestSeller: true,
      image: `${basePath}Sophos_XGS_2100.svg`,
      details: {
        Brand: "Sophos",
        Model: "XGS 2100",
        Type: "Next-Generation Firewall",
        Throughput: "11.5 Gbps Firewall, 3.5 Gbps NGFW",
        Interfaces: "8 x GE RJ45, 2 x GE SFP",
        "Form Factor": "1U Rack",
        "Best For": "Mid-size business",
      },
      about: `
        <ul>
          <li>Dual-processor architecture separates traffic handling from inspection workloads.</li>
          <li>TLS 1.3 inspection without the throughput penalty typical of this class.</li>
          <li>Synchronised Security links the firewall to Sophos endpoints to isolate compromised hosts automatically.</li>
          <li>Managed centrally through Sophos Central alongside endpoint and email protection.</li>
        </ul>
      `,
    },
    {
      name: "Sophos XGS 3100 Firewall",
      bestSeller: false,
      image: `${basePath}Sophos_XGS_3100.svg`,
      details: {
        Brand: "Sophos",
        Model: "XGS 3100",
        Type: "Next-Generation Firewall",
        Throughput: "18 Gbps Firewall, 5 Gbps NGFW",
        Interfaces: "8 x GE RJ45, 2 x GE SFP, expansion bay",
        "Form Factor": "1U Rack",
        "Best For": "Large campus",
      },
      about: `
        <ul>
          <li>Steps up inspection throughput for sites running heavy encrypted traffic.</li>
          <li>Flexi Port expansion bay allows 10GE or additional copper modules to be added later.</li>
          <li>Active-active high availability clustering for zero-downtime maintenance.</li>
          <li>Suits organisations consolidating several branch firewalls onto one appliance.</li>
        </ul>
      `,
    },

    // ==========================
    // BARRACUDA
    // ==========================
    {
      name: "Barracuda CloudGen Firewall F180",
      bestSeller: false,
      image: `${basePath}Barracuda_CloudGen_F180.svg`,
      details: {
        Brand: "Barracuda",
        Model: "CloudGen Firewall F180",
        Type: "SD-WAN Firewall",
        Throughput: "2.3 Gbps Firewall, 1.2 Gbps VPN",
        Interfaces: "8 x GE RJ45",
        "Form Factor": "Desktop",
        "Best For": "Multi-site SD-WAN",
      },
      about: `
        <ul>
          <li>Built around SD-WAN rather than bolting it on — designed for distributed branch networks.</li>
          <li>Traffic Intelligence balances sessions across multiple uplinks and fails over without dropping VPN tunnels.</li>
          <li>Zero-touch deployment lets a branch appliance configure itself on first boot.</li>
          <li>Native connectors for Azure and AWS transit networks.</li>
        </ul>
      `,
    },
    {
      name: "Barracuda Web Application Firewall 460",
      bestSeller: false,
      image: `${basePath}Barracuda_WAF_460.svg`,
      details: {
        Brand: "Barracuda",
        Model: "WAF 460",
        Type: "Web Application Firewall",
        Throughput: "1 Gbps HTTP, 400 Mbps HTTPS",
        Interfaces: "4 x GE RJ45",
        "Form Factor": "1U Rack",
        "Best For": "Public-facing web apps",
      },
      about: `
        <ul>
          <li>Protects web applications against the OWASP Top 10 — injection, cross-site scripting, broken access control.</li>
          <li>Terminates and inspects SSL/TLS, offloading crypto work from the application servers.</li>
          <li>Automatic learning builds a positive-security profile of normal application behaviour.</li>
          <li>Includes DDoS mitigation and bot defence at the application layer.</li>
        </ul>
      `,
    },

    // ==========================
    // F5
    // ==========================
    {
      name: "F5 BIG-IP i2800 Load Balancer",
      bestSeller: false,
      image: `${basePath}F5_BIG-IP_i2800.svg`,
      details: {
        Brand: "F5",
        Model: "BIG-IP i2800",
        Type: "Application Delivery Controller",
        Throughput: "10 Gbps L7, 5 Gbps SSL",
        Interfaces: "8 x 10GE SFP+",
        "Form Factor": "1U Rack",
        "Best For": "Application delivery",
      },
      about: `
        <ul>
          <li>Layer 7 load balancing with health monitoring and session persistence across application pools.</li>
          <li>Dedicated hardware SSL offload frees application servers from TLS handshake cost.</li>
          <li>iRules scripting allows traffic to be steered on any attribute of a request.</li>
          <li>Optional Advanced WAF module adds application-layer protection on the same appliance.</li>
        </ul>
      `,
    },

    // ==========================
    // CISCO
    // ==========================
    {
      name: "Cisco Firepower 1010 NGFW",
      bestSeller: true,
      image: `${basePath}Cisco_Firepower_1010.svg`,
      details: {
        Brand: "Cisco",
        Model: "Firepower 1010",
        Type: "Next-Generation Firewall",
        Throughput: "2 Gbps Firewall, 650 Mbps NGFW",
        Interfaces: "8 x GE RJ45",
        "Form Factor": "Desktop",
        "Best For": "Branch office / SMB",
      },
      about: `
        <ul>
          <li>Desktop NGFW running Cisco Secure Firewall software with Snort 3 inspection.</li>
          <li>Two of the eight ports support Power over Ethernet for a phone or access point.</li>
          <li>Managed locally through Firepower Device Manager or centrally through FMC.</li>
          <li>Fits branch sites that need Cisco-consistent policy without a rack.</li>
        </ul>
      `,
    },
    {
      name: "Cisco Firepower 2130 NGFW",
      bestSeller: false,
      image: `${basePath}Cisco_Firepower_2130.svg`,
      details: {
        Brand: "Cisco",
        Model: "Firepower 2130",
        Type: "Next-Generation Firewall",
        Throughput: "10 Gbps Firewall, 4.75 Gbps NGFW",
        Interfaces: "12 x GE RJ45, 4 x 10GE SFP+",
        "Form Factor": "1U Rack",
        "Best For": "Campus / datacenter edge",
      },
      about: `
        <ul>
          <li>Rack-mount NGFW with dedicated inspection hardware for consistent throughput under load.</li>
          <li>Integrates with Cisco Talos threat intelligence for continuously updated detection.</li>
          <li>Supports clustering for horizontal scale as traffic grows.</li>
          <li>Redundant, hot-swappable power supplies.</li>
        </ul>
      `,
    },

    // ==========================
    // PALO ALTO
    // ==========================
    {
      name: "Palo Alto PA-440 NGFW",
      bestSeller: false,
      image: `${basePath}Palo_Alto_PA-440.svg`,
      details: {
        Brand: "Palo Alto Networks",
        Model: "PA-440",
        Type: "Next-Generation Firewall",
        Throughput: "3.3 Gbps Firewall, 1.1 Gbps Threat Prevention",
        Interfaces: "8 x GE RJ45",
        "Form Factor": "Desktop",
        "Best For": "Branch office",
      },
      about: `
        <ul>
          <li>Identifies traffic by application rather than port, so policy is written in business terms.</li>
          <li>Fanless desktop chassis suitable for an office cupboard rather than a comms room.</li>
          <li>Cloud-delivered security subscriptions for URL filtering, DNS security and sandboxing.</li>
          <li>Managed at scale through Panorama alongside the rest of the estate.</li>
        </ul>
      `,
    },
  ],
};
